<template>
  <div class="work-calendar-wrapper" :class="!$store.state.bgLight ? 'theme-dark' : 'theme-light'">
    <div ref="calendarToolbar" class="calendar-toolbar">
      <div class="calendar-primary-actions">
        <a-button type="primary" :disabled="showAddBtn" icon="plus" @click="showModal">新增工作计划</a-button>
        <div class="calendar-navigation">
          <a-button icon="left" :aria-label="mode === 'month' ? '上个月' : '上一年'" :title="mode === 'month' ? '上个月' : '上一年'" @click="movePeriod(-1)" />
          <a-month-picker
            v-if="mode === 'month'"
            class="calendar-picker"
            :value="calendarValue"
            :allowClear="false"
            format="YYYY年MM月"
            @change="changeMonth" />
          <a-select v-else class="calendar-picker" :value="calendarValue.year()" aria-label="选择年份" @change="changeYear">
            <a-select-option v-for="year in yearOptions" :key="year" :value="year">{{ year }}年</a-select-option>
          </a-select>
          <a-button icon="right" :aria-label="mode === 'month' ? '下个月' : '下一年'" :title="mode === 'month' ? '下个月' : '下一年'" @click="movePeriod(1)" />
          <a-button @click="goToday">回到今天</a-button>
        </div>
      </div>
      <div class="calendar-secondary-actions">
        <work-plan-query-toggle v-if="isAdmin" :checked="queryAll" @change="changeQueryAll" />
        <div class="calendar-legend">
          <span class="legend-item">
            <span class="legend-dot status-pending"></span>
            <span class="legend-label">待完成 / 进行中</span>
          </span>
          <span class="legend-item">
            <span class="legend-dot status-completed"></span>
            <span class="legend-label">已完成</span>
          </span>
        </div>
        <a-radio-group :value="mode" size="small" @change="changeMode">
          <a-radio-button value="month">月视图</a-radio-button>
          <a-radio-button value="year">年视图</a-radio-button>
        </a-radio-group>
      </div>
    </div>
    <a-alert v-if="loadError" class="calendar-error" type="error" showIcon>
      <template slot="message">日程加载失败，<a @click="queryWorkPlan(calendarValue, type)">点击重试</a></template>
    </a-alert>
    <div ref="calendarBody" class="calendar-body" :style="{ '--calendar-cell-height': cellHeight + 'px' }">
      <a-spin :spinning="loading" tip="正在加载日程">
        <a-calendar :value="calendarValue" :mode="mode" :class="!$store.state.bgLight ? 'light' : ''" @panelChange="panelChange" @select="selectDate">
          <div slot="dateCellRender" slot-scope="value" class="events-cell-container">
            <div class="events-scroll-wrap">
              <button
                v-for="(item, index) in getDayPlans(value)"
                :key="item.id + '-' + index"
                type="button"
                class="plan-capsule-item"
                :class="item.detailStatus === 'success' ? 'status-completed' : 'status-pending'"
                :title="item.title"
                @click.stop="openItem(item)"
              >
                <span class="status-indicator"></span>
                <span class="plan-text">{{ item.title }}</span>
              </button>
            </div>
          </div>
          <template slot="monthCellRender" slot-scope="value">
            <div class="notes-month-container">
              <div v-if="getMonthData(value).numCo > 0" class="month-stat-tag stat-completed">已完成：{{ getMonthData(value).numCo }} 个</div>
              <div v-if="getMonthData(value).numNo > 0" class="month-stat-tag stat-pending">未完成：{{ getMonthData(value).numNo }} 个</div>
            </div>
          </template>
        </a-calendar>
      </a-spin>
    </div>
    <work-plan-modal ref="modalForm" @ok="modalFormOk"></work-plan-modal>
  </div>
</template>

<script>
import { getAction } from '@/api/manage'
import moment from 'moment'
import WorkPlanModal from './modules/WorkPlanModal.vue'
import WorkPlanQueryToggle from './modules/WorkPlanQueryToggle.vue'

export default {
  name: 'workCalendar',
  components: { WorkPlanModal, WorkPlanQueryToggle },
  data() {
    return {
      loading: false,
      loadError: false,
      queryAll: false,
      showAddBtn: false,
      listData: [],
      pagePath: '/om/work/plan/calendar/',
      findUrl: '/om/work/plan/',
      type: 2,
      mode: 'month',
      calendarValue: moment(),
      modeDate: moment(),
      hasActivated: false,
      cellHeight: 96,
      requestId: 0,
    }
  },
  computed: {
    isAdmin() {
      const user = this.$store.getters.userInfo
      return !!user && user.username === 'admin'
    },
    yearOptions() {
      const year = this.calendarValue.year()
      return Array.from({ length: 21 }, (_, index) => year - 10 + index)
    },
  },
  created() {
    this.queryWorkPlan(this.calendarValue, this.type)
  },
  mounted() {
    this.$nextTick(this.updateCalendarSize)
    window.addEventListener('resize', this.updateCalendarSize)
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateCalendarSize)
      this.resizeObserver.observe(this.$refs.calendarToolbar)
    }
  },
  activated() {
    if (this.hasActivated) this.queryWorkPlan(this.modeDate, this.type)
    this.hasActivated = true
    this.$nextTick(this.updateCalendarSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCalendarSize)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.requestId++
  },
  methods: {
    changeQueryAll(checked) {
      this.queryAll = checked
      this.queryWorkPlan(this.modeDate, this.type)
    },
    updateCalendarSize() {
      if (!this.$refs.calendarBody || !this.$el.getClientRects().length) return
      const bodyRect = this.$refs.calendarBody.getBoundingClientRect()
      const content = this.$el.closest('.ant-layout-content')
      const bottom = content ? Math.min(window.innerHeight, content.getBoundingClientRect().bottom) : window.innerHeight
      const top = bodyRect.top + (content ? content.scrollTop : 0)
      const paddingBottom = parseFloat(window.getComputedStyle(this.$el).paddingBottom) || 0
      // 扣除实测表头、行间距与页面留白，保留日期格内部滚动。
      const rows = this.mode === 'month' ? 6 : 4
      const availableHeight = bottom - top - paddingBottom - 12
      const cell = this.$refs.calendarBody.querySelector('.ant-fullcalendar-date, .ant-fullcalendar-month')
      if (!cell) return
      const renderedCellHeight = cell.getBoundingClientRect().height
      const extraHeight = bodyRect.height - renderedCellHeight * rows
      const cellHeight = Math.max(48, Math.floor((availableHeight - extraHeight) / rows))
      if (this.cellHeight !== cellHeight) this.cellHeight = cellHeight
    },
    panelChange(value, mode) {
      this.calendarValue = moment(value).clone()
      this.mode = mode
      this.type = mode === 'year' ? 1 : 2
      this.queryWorkPlan(this.calendarValue, this.type)
      this.$nextTick(this.updateCalendarSize)
    },
    movePeriod(offset) {
      this.panelChange(this.calendarValue.clone().add(offset, this.mode === 'month' ? 'months' : 'years'), this.mode)
    },
    changeMonth(value) {
      if (value) this.panelChange(value, 'month')
    },
    changeYear(year) {
      this.panelChange(this.calendarValue.clone().year(year), 'year')
    },
    goToday() {
      this.panelChange(moment(), 'month')
    },
    changeMode(event) {
      this.panelChange(this.calendarValue, event.target.value)
    },
    selectDate(value) {
      if (this.mode === 'year') {
        this.panelChange(value, 'month')
      } else if (!value.isSame(this.calendarValue, 'month')) {
        this.panelChange(value, 'month')
      } else {
        this.calendarValue = value.clone()
      }
    },
    queryWorkPlan(value, type) {
      this.modeDate = moment(value).clone()
      const time = this.modeDate.format(type === 1 ? 'YYYY' : 'YYYY-MM')
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = false
      this.listData = []
      // 快速切换月份时，只接受最后一次请求，避免旧月份覆盖当前视图。
      return getAction(this.pagePath + time + '/' + type, { queryAll: this.isAdmin && this.queryAll }).then((res) => {
        if (requestId !== this.requestId) return
        if (res.success) {
          this.listData = Array.isArray(res.result) ? res.result : []
        } else {
          this.loadError = true
        }
      }).catch(() => {
        if (requestId === this.requestId) this.loadError = true
      }).finally(() => {
        if (requestId === this.requestId) {
          this.loading = false
          this.$nextTick(this.updateCalendarSize)
        }
      })
    },
    getDayPlans(value) {
      const date = moment(value).format('YYYY-MM-DD')
      const data = this.listData.find((item) => item.dateStr === date)
      const plans = data && Array.isArray(data.result) ? data.result : []
      // 未完成优先，同一状态保持接口返回顺序。
      return plans.filter((item) => item.detailStatus !== 'success')
        .concat(plans.filter((item) => item.detailStatus === 'success'))
    },
    getMonthData(value) {
      const date = moment(value).format('YYYY-MM')
      return this.listData.find((item) => item.dateStr === date) || { numCo: 0, numNo: 0 }
    },
    showModal() {
      this.$refs.modalForm.add()
      this.$refs.modalForm.title = '新增：工作计划'
      this.$refs.modalForm.disableSubmit = false
    },
    modalFormOk() {
      this.queryWorkPlan(this.modeDate, this.type)
    },
    openItem(item) {
      this.selectItem(item.id)
    },
    selectItem(item) {
      getAction(this.findUrl + item).then((res) => {
        if (res.success) {
          this.$refs.modalForm.disableSubmit = true
          this.$refs.modalForm.edit(res.result)
          this.$refs.modalForm.title = '办理:工作计划'
          this.$refs.modalForm.isOK = res.result.status == 1 ? true : false
        } else {
          this.$message.info(res.message)
        }
      })
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@assets/less/common.less';

.work-calendar-wrapper {
  padding: 12px 16px;
  min-width: 0;
}

.calendar-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
  white-space: nowrap;

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    user-select: none;

    .legend-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;

      &.status-pending {
        background-color: #ffa940;
        box-shadow: 0 0 6px rgba(255, 169, 64, 0.5);
      }

      &.status-completed {
        background-color: #52c41a;
        box-shadow: 0 0 6px rgba(82, 196, 26, 0.8);
      }
    }
  }
}

.events-cell-container {
  height: 100%;
}

.events-scroll-wrap {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(128, 128, 128, 0.4);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.calendar-primary-actions,
.calendar-secondary-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.calendar-secondary-actions {
  margin-left: auto;
}

.calendar-navigation {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.calendar-picker {
  width: 156px;
}

.calendar-error {
  margin-bottom: 12px;
}

.calendar-body {
  /deep/ .ant-fullcalendar-header {
    display: none;
  }

  /deep/ .ant-fullcalendar {
    padding: 0;
  }

  /deep/ .ant-fullcalendar-date,
  /deep/ .ant-fullcalendar-month {
    height: var(--calendar-cell-height);
    padding: 4px 6px;
    overflow: hidden;
  }

  /deep/ .ant-fullcalendar-content {
    height: calc(var(--calendar-cell-height) - 34px);
    overflow: hidden;
  }
}

.plan-capsule-item {
  display: flex;
  align-items: center;
  padding: 3px 7px;
  margin-bottom: 4px;
  width: 100%;
  height: 24px;
  text-align: left;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.35;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  }

  .status-indicator {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 6px;
  }

  .plan-text {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 500;
    letter-spacing: 0.2px;
  }
}

/* 月统计展示标签 */
.notes-month-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  padding: 4px;
}

.month-stat-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  line-height: 1.4;
  font-weight: 500;
}

/* 适配大屏深色主题 (theme-dark) */
.theme-dark {
  .calendar-legend .legend-item {
    color: rgba(255, 255, 255, 0.85);
  }

  .plan-capsule-item {
    /* 使用明亮橙色实底和深色文字，与深蓝背景及已完成的绿色区分。 */
    &.status-pending {
      background: #ffc069;
      border: 1px solid #fa8c16;
      color: #612500;

      .status-indicator {
        background-color: #ad4e00;
      }

      &:hover {
        background: #ffd591;
        border-color: #d46b08;
        color: #612500;
      }
    }

    /* 已完成 - 生机翡翠绿胶囊 */
    &.status-completed {
      background: rgba(82, 196, 26, 0.16);
      border: 1px solid rgba(82, 196, 26, 0.45);
      color: #b7eb8f;

      .status-indicator {
        background-color: #52c41a;
        box-shadow: 0 0 5px #52c41a;
      }

      &:hover {
        background: rgba(82, 196, 26, 0.28);
        border-color: #52c41a;
        color: #ffffff;
      }
    }
  }

  .month-stat-tag {
    &.stat-completed {
      background: rgba(82, 196, 26, 0.18);
      border: 1px solid rgba(82, 196, 26, 0.45);
      color: #b7eb8f;
    }

    &.stat-pending {
      background: #ffc069;
      border: 1px solid #fa8c16;
      color: #612500;
    }
  }

  /* 穿透定制 antd calendar 深色大屏表现 */
  /deep/ .ant-fullcalendar {
    border-color: rgba(255, 255, 255, 0.1);
  }

  /deep/ .ant-fullcalendar-column-header {
    color: rgba(255, 255, 255, 0.85) !important;
  }

  /deep/ .ant-fullcalendar-date,
  /deep/ .ant-fullcalendar-month {
    border-top: 2px solid rgba(255, 255, 255, 0.12) !important;
    transition: background-color 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
  }

  /deep/ .ant-fullcalendar-date:hover,
  /deep/ .ant-fullcalendar-month:hover {
    background: rgba(24, 144, 255, 0.1) !important;
    box-shadow: inset 0 0 0 1px #40a9ff, 0 0 6px rgba(64, 169, 255, 0.2);
  }

  /deep/ .ant-fullcalendar-date:active,
  /deep/ .ant-fullcalendar-month:active {
    background: rgba(24, 144, 255, 0.16) !important;
  }

  /deep/ .ant-fullcalendar-value {
    color: rgba(255, 255, 255, 0.85) !important;
    font-weight: 500;
  }

  /deep/ .ant-fullcalendar-last-month-cell .ant-fullcalendar-value,
  /deep/ .ant-fullcalendar-next-month-btn-day .ant-fullcalendar-value {
    color: rgba(255, 255, 255, 0.28) !important;
  }

  /* 月、年视图共用选中效果，内描边不改变格子的尺寸。 */
  /deep/ .ant-fullcalendar-selected-day .ant-fullcalendar-date,
  /deep/ .ant-fullcalendar-month-panel-selected-cell .ant-fullcalendar-month {
    background: rgba(24, 144, 255, 0.16) !important;
    box-shadow: inset 0 0 0 2px #40a9ff, inset 0 0 12px rgba(64, 169, 255, 0.2), 0 0 8px rgba(64, 169, 255, 0.35) !important;
  }

  /deep/ .ant-fullcalendar-selected-day .ant-fullcalendar-value,
  /deep/ .ant-fullcalendar-month-panel-selected-cell .ant-fullcalendar-value {
    color: #ffffff !important;
    font-weight: bold;
    text-shadow: 0 0 6px rgba(24, 144, 255, 0.8);
  }

  /deep/ .ant-fullcalendar-today .ant-fullcalendar-date,
  /deep/ .ant-fullcalendar-month-panel-current-cell .ant-fullcalendar-month {
    border-top-color: #1890ff !important;
  }
}

/* 适配普通浅色主题 (theme-light) */
.theme-light {
  .calendar-legend .legend-item {
    color: rgba(0, 0, 0, 0.65);
  }

  .plan-capsule-item {
    &.status-pending {
      background: #ffc069;
      border: 1px solid #fa8c16;
      color: #612500;

      .status-indicator {
        background-color: #ad4e00;
      }

      &:hover {
        background: #ffd591;
        border-color: #d46b08;
        color: #612500;
      }
    }

    &.status-completed {
      background: #f6ffed;
      border: 1px solid #b7eb8f;
      color: #389e0d;

      .status-indicator {
        background-color: #52c41a;
      }

      &:hover {
        background: #d9f7be;
        border-color: #52c41a;
        color: #237804;
      }
    }
  }

  .month-stat-tag {
    &.stat-completed {
      background: #f6ffed;
      border: 1px solid #b7eb8f;
      color: #389e0d;
    }

    &.stat-pending {
      background: #ffc069;
      border: 1px solid #fa8c16;
      color: #612500;
    }
  }
}
@media (max-width: 768px) {
  .work-calendar-wrapper {
    padding: 8px 0;
  }

  .calendar-legend {
    gap: 10px;
    font-size: 12px;
  }

  .calendar-body /deep/ .ant-fullcalendar-date {
    margin: 0 1px;
    padding: 4px 2px;
  }

  .calendar-body .plan-capsule-item {
    padding: 3px;
  }
}
</style>
